import { Form, RadioInputField, TextInputField } from '@repo/ui/Form';

import { TypeOf, z } from 'zod';

import { Dialog } from '@repo/ui/Dialog';
import { Button } from '@repo/ui/Button';
import type { Mercury } from '@repo/types/Models';
import { useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

const updateIpFormSchema = z.object({
  address: z.union([z.ipv4(), z.ipv6()], { error: 'Invalid IP address.' }),
  name: z.string().min(2, { message: 'Name must contain at least 2 characters.' }),
  scope: z.enum(['API', 'GQL', 'ANY']),
});

type UpdateIpFormParams = TypeOf<typeof updateIpFormSchema>;

interface UpdateIpFormProps {
  refetch: () => void;
  address: Mercury.Models.AccountAllowedAccessIp['address'];
  name: Mercury.Models.AccountAllowedAccessIp['name'];
  scope: Mercury.Models.AccountAllowedAccessIp['scope'];
  id: Mercury.Models.AccountAllowedAccessIp['id'];
  loading: boolean;
}

//TODO: Refactor forms in dialog so only the freshest data is reflected in dialogs,
//  before, during and after submissions

// Problem 1, new property is added to FormProvider which may need to be fixed in the future depending on library updates
// Problem 2, how to visualize data changes in filtered views of data
// Problem 3, question of what values are shown in the first instance of a form? Data from the list, or refetched data from the server?
// Problem 4, When and how do you update the content of tables after an item's data has changed?

// Imagine data changeing from multiple users, in multiple views with multiple filters

//TODO: fix "already has bee taken error"

const UpdateIpForm = ({ refetch, address, name, scope, id, loading }: UpdateIpFormProps) => {
  const UPDATE_ACCOUNT_ALLOWED_ACCESS_IP = gql`
    mutation UpdateAccountAllowedAccessIp($id: ID!, $input: UpdateAccountAllowedAccessIpInput!) {
      updateAccountAllowedAccessIp(id: $id, input: $input) {
        accountAllowedAccessIp {
          id
        }
      }
    }
  `;

  const client = useApolloClient();

  const onSubmit = async ({ ...input }: UpdateIpFormParams) => {
    await client.mutate({
      mutation: UPDATE_ACCOUNT_ALLOWED_ACCESS_IP,
      variables: {
        id: id,
        input,
      },
    });

    refetch();
  };

  const form = useZodForm({
    schema: updateIpFormSchema,
    onSubmit,

    defaultValues: {
      address: address,
      name: name,
      scope: scope,
    },
  });

  return (
    <Dialog
      title={`Update IP Address: ${address}`}
      centered
      trigger={{ label: 'Update', size: 'sm' }}
    >
      <Form {...form}>
        <Form.Success fixed={true}>IP address successfully updated.</Form.Success>
        <Form.Error>
          Unable to update IP address. Please try again later or contact support if error persists.
          error persists.
        </Form.Error>

        <TextInputField
          label="Address"
          defaultValue={address}
          {...registerInput(form, 'address')}
        />

        <TextInputField label="Name" defaultValue={name} {...registerInput(form, 'name')} />
        <div className="flex items-center gap-5 sm:col-span-2">
          <RadioInputField
            label="API"
            {...registerInput(form, 'scope')}
            value="API"
            defaultChecked={scope === 'API'}
          />
          <RadioInputField
            label="Dashboard"
            {...registerInput(form, 'scope')}
            value="GQL"
            defaultChecked={scope === 'GQL'}
          />
          <RadioInputField
            label="Any"
            {...registerInput(form, 'scope')}
            value="ANY"
            defaultChecked={scope === 'ANY'}
          />
        </div>
        <Button type="submit">Update</Button>
      </Form>
    </Dialog>
  );
};

export default UpdateIpForm;
